<template>
    <Layout>
        <div v-if="error" class="flex flex-col items-center justify-center space-y-8 p-32">
            <p class="text-xl font-bold">{{ $t('This formation is no longer available.') }}</p>
            <p>
                <InertiaLink href="/formations" class="button">{{ $t('See other formations') }}</InertiaLink>
            </p>
        </div>

        <div v-else>
            <!-- Page heading -->
            <div>
                <img class="h-48 w-full object-cover lg:h-64" :src="formation.banner" alt="" />

                <div class="mx-auto -mt-16 max-w-6xl">
                    <!-- Logo & title -->
                    <div class="flex">
                        <a
                            :href="`https://${formation.company.subdomain}.camaq.ca`"
                            class="z-10 hidden h-36 w-48 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-gray-300 bg-white md:flex"
                        >
                            <img :src="formation.company.logo" alt="" class="h-32 w-32 object-contain" />
                        </a>

                        <div class="-ml-10 flex flex-1 items-center">
                            <div
                                class="flex flex-1 flex-col items-center justify-between space-y-6 rounded-lg border bg-white p-6 pl-16 shadow-lg md:flex-row md:space-y-0"
                            >
                                <div class="max-w-[300px] overflow-hidden text-ellipsis sm:max-w-[550px] md:max-w-[450px] lg:max-w-[600px]">
                                    <h1 class="text-3xl font-bold text-gray-900 xl:line-clamp-3" :title="formation.title">
                                        {{ formation.title }}
                                    </h1>
                                </div>

                                <div class="flex space-x-4">
                                    <button class="button" @click.prevent="inscriptionModalRef.show()">
                                        {{ $t('Sign up') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Info cards (formation information, salary, address, etc) -->
                    <div class="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <div class="badge" v-if="formation.city?.length">
                            <IconMapPin :size="16" />
                            <span>{{ formation.address }}, {{ formation.city }}</span>
                        </div>

                        <div v-if="formation.starts_at" class="badge">
                            <IconCalendar :size="16" />
                            <span>{{ $t('Starts :') }}</span>
                            <span>{{ formation.starts_at }}</span>
                        </div>

                        <div v-if="formation.ends_at" class="badge">
                            <IconCalendar :size="16" />
                            <span>{{ $t('Ends :') }}</span>
                            <span>{{ formation.ends_at }}</span>
                        </div>

                        <div v-if="formation.cost" class="badge">
                            <IconCurrencyDollar :size="16" />
                            <span>{{ formation.cost }}</span>
                        </div>

                        <div v-if="formation.number_openings" class="badge">
                            <IconUsersGroup :size="16" />
                            <span>{{ $t('Number of places :') }}</span>
                            <span>{{ formation.number_openings }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Description -->
            <div class="mx-auto mt-8 flex max-w-5xl justify-center px-8">
                <div class="prose lg:prose-lg" v-html="description"></div>
            </div>

            <div class="my-8 lg:hidden">
                <SocialSharingInline />
            </div>

            <!-- Social sharing links -->
            <SocialSharing />

            <!-- Sticky application bar -->
            <div
                v-if="formation.custom_form_fields?.length"
                class="fixed inset-x-0 bottom-0 flex flex-col items-center justify-center space-y-4 border-t bg-white p-4 shadow md:flex-row md:space-x-8 md:space-y-0"
            >
                <p class="font-medium">Cette formation vous intéresse?</p>

                <button class="button" @click.prevent="inscriptionModalRef.show()">
                    {{ $t('Sign up') }}
                </button>
            </div>
        </div>

        <FormationInscriptionModal ref="inscriptionModalRef" :formation="formation" />
    </Layout>
</template>

<script setup>
    import FormationInscriptionModal from '@/Partials/FormationInscriptionModal';
    import SocialSharing from '@/Partials/SocialSharing';
    import SocialSharingInline from '@/Partials/SocialSharingInline';
    import { useCookieConsent } from '@/useCookieConsent';
    import { IconCalendar, IconCurrencyDollar, IconMapPin, IconUsersGroup } from '@tabler/icons-vue';
    import { onBeforeMount, ref } from 'vue';

    const { consent, replaceYoutubeIframes } = useCookieConsent();

    const props = defineProps({
        error: Boolean,
        formation: Object,
    });

    const description = ref(props.formation?.description);

    const inscriptionModalRef = ref(null);

    onBeforeMount(() => {
        if (consent != 'accept-all') {
            description.value = replaceYoutubeIframes(props.formation?.description);
        }
    });
</script>
